import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import * as db from "./lib/db";
import { PRESET_MEALS } from "./data/meals";
import { DEFAULT_TEMPLATES } from "./data/exercises";
import { genId, todayKey, dateKey } from "./utils/helpers";
import { track } from "./lib/analytics";
import * as moodStore from "./lib/mood-store";
import { appFor } from "./lib/product";
import { calculateMacros, getTodayMacros } from "./utils/macros";
import { calcTodayBurn, adjustMacrosForBurn } from "./utils/calorie-burn";
import { syncRunToHealth, syncWeightToHealth, requestHealthAuthorization } from "./utils/healthkit";

// Hooks
import { useAuth } from "./hooks/useAuth";
import { useAppData } from "./hooks/useAppData";
import { useConnectivity } from "./hooks/useConnectivity";
import { useWorkoutActions } from "./hooks/useWorkoutActions";
import { useMealActions } from "./hooks/useMealActions";
import { useRunActions } from "./hooks/useRunActions";
import { useGamification } from "./hooks/useGamification";
import { useNotifications } from "./hooks/useNotifications";

// Components
import AuthScreen from "./components/AuthScreen";
import ResetPasswordScreen from "./components/ResetPasswordScreen";
import OnboardingFlow from "./components/OnboardingFlow";
import MacroResults from "./components/MacroResults";
import Header from "./components/Header";
import BottomNav from "./components/BottomNav";
import PRCelebration from "./components/PRCelebration";
import OfflineBanner from "./components/OfflineBanner";
import EveningNote from "./components/EveningNote";
import ShareCard from "./components/ShareCard";
import StreakMilestone, { shouldShowStreakMilestone, markStreakMilestoneShown } from "./components/StreakMilestone";

// Pages — HomeTab stays eager (it's the first paint); the rest load on demand
// so the initial bundle stays small and the app opens fast.
import HomeTab from "./pages/HomeTab";
const MealsTab = lazy(() => import("./pages/MealsTab"));
const TrainTab = lazy(() => import("./pages/TrainTab"));
const ProfileTab = lazy(() => import("./pages/ProfileTab"));
const ClinicianApp = lazy(() => import("./pages/ClinicianApp"));
const RunTab = lazy(() => import("./pages/RunTab"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

// Modals — all behind user interaction, safe to load on demand
const ActiveWorkout = lazy(() => import("./modals/ActiveWorkout"));
const WorkoutDetail = lazy(() => import("./modals/WorkoutDetail"));
const NewWorkoutModal = lazy(() => import("./modals/NewWorkoutModal"));
const AddExerciseModal = lazy(() => import("./modals/AddExerciseModal"));
const NewExerciseModal = lazy(() => import("./modals/NewExerciseModal"));
const CustomMealModal = lazy(() => import("./modals/CustomMealModal"));
const LogRunModal = lazy(() => import("./modals/LogRunModal"));
const RunDetail = lazy(() => import("./modals/RunDetail"));
const LogWeightModal = lazy(() => import("./modals/LogWeightModal"));
const MacroGoalsModal = lazy(() => import("./modals/MacroGoalsModal"));
const PostWorkoutSummary = lazy(() => import("./modals/PostWorkoutSummary"));
const CoachChat = lazy(() => import("./modals/CoachChat"));

import * as aiLib from "./lib/ai";
import { hapticMedium } from "./utils/haptics";
import { getNextMilestone } from "./utils/pr-trajectory";
import { enqueue, getPendingCount } from "./utils/offline-queue";
import { useToast } from "./hooks/useToast";

import "./styles.css";

export default function App() {
  // ---- Core hooks ----
  const toast = useToast();
  const auth = useAuth();
  const { isOnline, pendingSyncs, setPendingSyncs } = useConnectivity();
  const { notificationsEnabled, handleEnableNotifications } = useNotifications();

  const data = useAppData(auth.userId, { setLoading: auth.setLoading });

  const workoutActions = useWorkoutActions({
    userId: auth.userId,
    workouts: data.workouts,
    exercises: data.exercises,
    personalRecords: data.personalRecords,
    todayAiWorkout: data.todayAiWorkout,
    runs: data.runs,
    onboardingCompleted: data.onboardingCompleted,
    isOnline,
    loading: auth.loading,
    userProfile: data.userProfile,
    onSaveMood: (m) => handleSaveMoodRef.current?.(m),
    // Today's morning check-in — the input every generated session is sized to.
    todayCheckin: (data.moodCheckins || []).find(
      (c) => c.context === "daily" && c.date === todayKey()
    ),
    setTodayAiWorkout: data.setTodayAiWorkout,
    setWorkouts: data.setWorkouts,
    setExercises: data.setExercises,
    setPersonalRecords: data.setPersonalRecords,
    setPendingSyncs,
  });

  const mealActions = useMealActions({
    userId: auth.userId,
    mealLog: data.mealLog,
    isOnline,
    setMealLog: data.setMealLog,
    setCustomMeals: data.setCustomMeals,
    setPendingSyncs,
    allMeals: [...PRESET_MEALS, ...data.customMeals],
    macroGoals: data.macroGoals,
  });

  const runActions = useRunActions({
    userId: auth.userId,
    setRuns: data.setRuns,
    setRunPlan: data.setRunPlan,
    setRunPlanLoading: data.setRunPlanLoading,
  });

  const gamification = useGamification(
    data.workouts,
    data.runs,
    data.mealLog,
    data.personalRecords
  );

  // Ref bridge: workoutActions is created before handleSaveMood exists.
  const handleSaveMoodRef = useRef(null);

  // ---- UI-only state ----
  const [tab, setTab] = useState("home");

  /* A clinician lands in their caseload, not in somebody else's daily ritual.
     Routing only — who may READ whose readings is decided by row-level
     security in Postgres, where editing the bundle cannot reach it. */
  useEffect(() => {
    if (appFor(data.profile) === "clinician") setTab("clinician");
  }, [data.profile]);
  const [trainSubTab, setTrainSubTab] = useState(null); // null = use TrainTab default
  const [modal, setModal] = useState(null);
  const [viewWorkout, setViewWorkout] = useState(null);
  const [viewRun, setViewRun] = useState(null);
  const [prCelebration, setPrCelebration] = useState(null);
  const [shareCard, setShareCard] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [scheduledRunForLog, setScheduledRunForLog] = useState(null);
  const [streakMilestone, setStreakMilestone] = useState(null);

  // ---- Sign out: reset all hook state ----
  const handleSignOut = useCallback(async () => {
    // Mood data is sensitive and device-local — clear it before the session ends
    // so it can never surface under a different account on a shared device.
    moodStore.clearLocalCheckins(auth.userId);
    await auth.handleSignOut();
    data.reset();
    workoutActions.reset();
    setTab("home");
    setModal(null);
  }, [auth, data, workoutActions]);

  // ---- Streak milestone detection ----
  useEffect(() => {
    const streak = gamification.currentStreak;
    if (streak > 0 && shouldShowStreakMilestone(streak)) {
      // Small delay so the home screen renders first
      const t = setTimeout(() => setStreakMilestone(streak), 800);
      return () => clearTimeout(t);
    }
  }, [gamification.currentStreak]);

  // ---- Mood check-in (the core loop) ----
  const handleSaveMood = useCallback(async ({ mood, energy, note, context, workoutId }) => {
    if (!auth.userId) return;
    const payload = { date: todayKey(), mood, energy, note, context: context || "daily", workoutId };
    // Local-first: this always succeeds and is immediately readable. Syncing to
    // Supabase happens in the background and is allowed to fail (table missing,
    // project paused, no signal) without the user ever seeing an error.
    const saved = moodStore.saveCheckin(auth.userId, payload);
    data.setMoodCheckins((prev) => [...prev.filter((c) => c.id !== saved.id), saved]);
    track("mood_checkin", { context: payload.context, mood, energy });
    if (payload.context === "daily") track("daily_checkin_completed", { mood, energy });
    // Returned so callers can attach a note to this exact record afterwards.
    return saved;
  }, [auth.userId, data]);

  /**
   * The optional note arrives after the reading has already been written (see
   * updateCheckinNote). Local-first, same as everything else here.
   */
  const handleSaveMoodNote = useCallback((id, note) => {
    if (!auth.userId || !id) return;
    const updated = moodStore.updateCheckinNote(auth.userId, id, note);
    if (updated) data.setMoodCheckins((prev) => prev.map((c) => (c.id === id ? updated : c)));
  }, [auth.userId, data]);

  // Keep the ref current for consumers created before this handler exists
  // (workoutActions). Assigning during render violates the rules of hooks.
  useEffect(() => {
    handleSaveMoodRef.current = handleSaveMood;
  }, [handleSaveMood]);

  // ---- AI workout generation (bridges hook + data) ----
  const handleGenerateWorkout = useCallback(async (context) => {
    const result = await workoutActions.handleGenerateWorkout(context);
    if (result) data.setTodayAiWorkout(result);
  }, [workoutActions, data]);

  // ---- Request HealthKit authorization once logged in (iOS only, no-op on web) ----
  useEffect(() => {
    if (auth.userId) requestHealthAuthorization().catch((e) => console.error("HealthKit authorization failed:", e));
  }, [auth.userId]);

  // ---- Recover macros if onboarding saved but user didn't confirm ----
  useEffect(() => {
    if (
      !auth.loading &&
      data.onboardingCompleted &&
      !data.pendingMacros &&
      !data.macroGoals &&
      data.userProfile?.weight
    ) {
      const recovered = calculateMacros({
        weight: data.userProfile.weight,
        heightFeet: data.userProfile.heightFeet || data.userProfile.height_feet,
        heightInches: data.userProfile.heightInches || data.userProfile.height_inches,
        age: data.userProfile.age,
        sex: data.userProfile.sex,
        trainingDaysPerWeek: data.userProfile.trainingDaysPerWeek || data.userProfile.training_days_per_week,
        goal: data.userProfile.primaryGoal || data.userProfile.primary_goal,
        targetWeight: data.targetWeight,
      });
      data.setPendingMacros(recovered);
    }
  }, [auth.loading, data]);

  // ---- Computed ----
  const allMeals = [...PRESET_MEALS, ...data.customMeals];
  const todayMeals = data.mealLog[todayKey()] || [];
  const todayTotals = todayMeals
    .filter((e) => e.mealId !== "__water__")
    .reduce(
      (acc, entry) => {
        const meal = allMeals.find((m) => m.id === entry.mealId);
        if (!meal) return acc;
        const s = parseFloat(entry.servings) || 0;
        return {
          calories: acc.calories + meal.calories * s,
          protein: acc.protein + meal.protein * s,
          carbs: acc.carbs + meal.carbs * s,
          fat: acc.fat + meal.fat * s,
          fiber: acc.fiber + meal.fiber * s,
        };
      },
      { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
    );

  const currentWeight = data.userProfile?.weight || (data.weightLog || [])[0]?.weight;
  const todayBurn = calcTodayBurn(data.workouts, data.runs, currentWeight, data.exercises);
  const hasActivityToday =
    data.workouts.some((w) => dateKey(w.startTime) === todayKey()) ||
    (data.runs || []).some((r) => dateKey(r.timestamp) === todayKey()) ||
    (data.mealLog[todayKey()] || []).some((m) => m.mealId !== "__water__");

  const todayHasWorkout =
    data.workouts.some((w) => dateKey(w.startTime) === todayKey()) ||
    !!workoutActions.activeWorkout;

  const baseMacros = data.macroGoals?.training_day
    ? (todayHasWorkout ? data.macroGoals.training_day : data.macroGoals.rest_day)
    : getTodayMacros(data.macroGoals);
  const todayGoals =
    todayBurn.total > 0 && baseMacros
      ? adjustMacrosForBurn(
          baseMacros,
          todayBurn.total,
          todayHasWorkout,
          data.userProfile?.primaryGoal || data.userProfile?.primary_goal
        )
      : baseMacros;

  // ---- PR callback (UI lives here) ----
  const handleCheckPR = useCallback(
    (exerciseId, exerciseName, weight, reps) =>
      workoutActions.checkAndSetPR(exerciseId, exerciseName, weight, reps, setPrCelebration),
    [workoutActions]
  );

  // ---- Password reset ---- (arrived from a reset link — must set a new
  // password before anything else, even though a recovery session exists)
  if (auth.recovery) return <ResetPasswordScreen onDone={auth.clearRecovery} />;

  // ---- Backend unreachable ----
  // Shown instead of an endless spinner when auth can't reach the server
  // (paused project, offline, DNS). Failing loudly beats failing silently.
  if (auth.authError && !auth.session) {
    return (
      <div className="loading-screen" style={{ padding: 32, textAlign: "center", gap: 16 }}>
        <div className="loading-logo brand-logo">
          <span className="brand-mark">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M15 4L22 18H8L15 4Z" fill="currentColor" opacity="0.3" />
              <path d="M9 8L17 18H1L9 8Z" fill="currentColor" />
            </svg>
          </span>
          <span className="brand-text brand-text--md">elevate</span>
        </div>
        <div style={{ fontSize: 15, color: "var(--text)", marginTop: 8 }}>Can&apos;t connect</div>
        <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6, maxWidth: 300 }}>
          {auth.authError}
        </div>
        {/* Diagnostic: which backend are we actually calling? The project URL is
            a public identifier (it's in every request the browser makes), so
            showing it is safe and turns "it's broken" into an answerable question. */}
        <div
          style={{
            marginTop: 4, fontSize: 10, color: "var(--text3)", lineHeight: 1.5,
            maxWidth: 320, wordBreak: "break-all", fontFamily: "'DM Mono', monospace",
          }}
        >
          backend: {(() => {
            try {
              return new URL(import.meta.env.VITE_SUPABASE_URL).host;
            } catch {
              return "NOT CONFIGURED";
            }
          })()}
        </div>
        <button
          className="btn btn-primary btn-h44"
          style={{ marginTop: 8, minWidth: 160 }}
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  // ---- Loading ----
  if (auth.loading)
    return (
      <div className="loading-screen">
        <div className="loading-logo brand-logo">
          <span className="brand-mark">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M15 4L22 18H8L15 4Z" fill="currentColor" opacity="0.3" />
              <path d="M9 8L17 18H1L9 8Z" fill="currentColor" />
            </svg>
          </span>
          <span className="brand-text brand-text--md">elevate</span>
        </div>
        <div className="loading-dots">
          <span /><span /><span />
        </div>
      </div>
    );

  if (!auth.session) return <AuthScreen onAuth={auth.handleAuth} />;
  if (window.location.hash === "#admin") {
    return (
      <Suspense fallback={null}>
        <AdminDashboard />
      </Suspense>
    );
  }

  if (data.onboardingCompleted !== true) {
    return (
      <OnboardingFlow
        username={data.profile?.username}
        onComplete={async (formData) => {
          if (!data.profile) {
            const fallbackName =
              auth.session?.user?.user_metadata?.username ||
              auth.session?.user?.email?.split("@")[0] ||
              "user";
            let p;
            try {
              p = await db.ensureProfile(auth.userId, fallbackName);
            } catch {
              const suffixed = `${fallbackName.slice(0, 16)}_${Math.random().toString(36).slice(2, 6)}`;
              p = await db.ensureProfile(auth.userId, suffixed);
            }
            data.setProfile(p);
          }
          await db.saveOnboarding(auth.userId, formData);
          data.setUserProfile(formData);
          if (formData.targetWeight) {
            const tw = parseFloat(formData.targetWeight);
            if (tw > 0) {
              data.setTargetWeight(tw);
              await db.updateWeightSettings(auth.userId, { targetWeight: tw });
            }
          }
          if (formData.weight) {
            const entry = { id: genId(), weight: parseFloat(formData.weight), date: todayKey() };
            data.setWeightLog((prev) => [entry, ...prev]);
            try { await db.insertWeightEntry(auth.userId, entry); } catch { /* ignored */ }
          }
          const macros = calculateMacros({
            weight: formData.weight,
            heightFeet: formData.heightFeet,
            heightInches: formData.heightInches,
            age: formData.age,
            sex: formData.sex,
            trainingDaysPerWeek: formData.trainingDaysPerWeek,
            goal: formData.primaryGoal,
            targetWeight: formData.targetWeight || data.targetWeight,
          });
          data.setOnboardingCompleted(true);
          data.setPendingMacros(macros);
          track("onboarding_completed", { goal: formData.primaryGoal, days_per_week: formData.trainingDaysPerWeek });
          // Magic moment: start building their first workout NOW, while they
          // review their macros — it's waiting when they land on Home.
          workoutActions.handleQuickGenerate({
            available_time: parseInt(String(formData.sessionDuration || "").match(/\d+/g)?.pop(), 10) || 45,
          });
        }}
      />
    );
  }

  if (data.pendingMacros) {
    return (
      <MacroResults
        macros={data.pendingMacros}
        onConfirm={async () => {
          const goals = data.pendingMacros.daily;
          data.setMacroGoals(goals);
          data.setOnboardingCompleted(true);
          data.setPendingMacros(null);
          try { await db.updateMacroGoals(auth.userId, goals); } catch (e) {
            console.error("Failed to save macros:", e);
          }
        }}
        onBack={() => {
          data.setPendingMacros(null);
          data.setOnboardingCompleted(false);
        }}
      />
    );
  }

  // ---- Main app ----
  return (
    <>
      <OfflineBanner isOnline={isOnline} pendingSyncs={pendingSyncs} />
      <div className="app-shell">
        <a href="#main-content" className="skip-link">Skip to content</a>
        {data.loadFailed && (
          <div
            role="alert"
            style={{
              margin: "8px 16px 0", padding: "10px 14px", borderRadius: 10,
              background: "rgba(181, 86, 78, 0.08)", border: "1px solid var(--danger)",
              fontSize: 12, color: "var(--text2)", lineHeight: 1.5,
            }}
          >
            Couldn&apos;t reach the server, so your data may be out of date.{" "}
            <button
              className="btn-reset"
              style={{ color: "var(--accent)", fontSize: 12, textDecoration: "underline" }}
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}
        <Header username={data.profile?.username} onSignOut={handleSignOut} />

        <Suspense
          fallback={
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
              <div className="loading-dots"><span /><span /><span /></div>
            </div>
          }
        >
        {/* THE CLINICIAN APP.
            Both of its screens existed, were tested and were screenshotted
            while being reachable from nothing — a surface only the preview
            harness can render has not shipped. It takes over the whole app for
            a clinician account, and is openable by anyone else from Profile so
            the thing being sold can actually be looked at. */}
        {tab === "clinician" && (
          <ClinicianApp
            userId={auth.userId}
            clinicianName={data.profile?.username}
            onExit={() => setTab("home")}
          />
        )}

        {tab === "home" && (
          <HomeTab
            username={data.profile?.username}
            workouts={data.workouts}
            runs={data.runs}
            todayTotals={todayTotals}
            todayBurn={todayBurn}
            currentStreak={gamification.currentStreak}
            userLevel={gamification.userLevel}
            levelProgress={gamification.levelProgress}
            achievements={gamification.achievements}
            macroGoals={todayGoals}
            exercises={data.exercises}
            mealLog={data.mealLog}
            personalRecords={data.personalRecords}
            weightLog={data.weightLog}
            userProfile={data.userProfile}
            allMeals={allMeals}
            targetWeight={data.targetWeight}
            weightReminderTime={data.weightReminderTime}
            onStartWorkout={() => setModal("newWorkout")}
            onLogRun={(scheduled) => { setScheduledRunForLog(scheduled || null); setModal("logRun"); }}
            onViewWorkout={setViewWorkout}
            onViewRun={setViewRun}
            onNavigateToNutrition={() => setTab("nutrition")}
            onLogWeight={() => setModal("logWeight")}
            todayAiWorkout={data.todayAiWorkout}
            aiWorkoutLoading={workoutActions.aiWorkoutLoading}
            aiWorkoutError={workoutActions.aiWorkoutError}
            onClearAiError={() => workoutActions.setAiWorkoutError(null)}
            onGenerateWorkout={handleGenerateWorkout}
            onQuickGenerate={workoutActions.handleQuickGenerate}
            onBeginAiWorkout={() => workoutActions.handleBeginAiWorkout(data.todayAiWorkout)}
            onRegenerateWorkout={() => data.setTodayAiWorkout(null)}
            userId={auth.userId}
            onEnableNotifications={handleEnableNotifications}
            notificationsEnabled={notificationsEnabled}
            aiInsights={data.aiInsights}
            userId={auth.userId}
            moodCheckins={data.moodCheckins}
            moodLoaded={data.moodLoaded}
            onSaveMood={handleSaveMood}
            onSaveMoodNote={handleSaveMoodNote}
            onLogMeal={mealActions.logMeal}
            onAddCustomMeal={() => setModal("customMeal")}
            onRemove={mealActions.removeMealEntry}
            onUpdateServings={mealActions.updateMealServings}
            onRefresh={data.handleRefresh}
            isRefreshing={data.isRefreshing}
            onShareStreak={() => setShareCard({ type: "streak", data: { days: gamification.currentStreak } })}
            runPlan={data.runPlan}
            onViewRunPlan={() => { setTab("train"); setTrainSubTab("programs"); }}
          />
        )}

        {tab === "train" && (
          <TrainTab
            exercises={data.exercises}
            workouts={data.workouts}
            runs={data.runs}
            personalRecords={data.personalRecords}
            userTemplates={data.userTemplates}
            onAddExercise={() => setModal("newExercise")}
            onStartWorkout={() => setModal("newWorkout")}
            onLogRun={(scheduled) => { setScheduledRunForLog(scheduled || null); setModal("logRun"); }}
            onViewWorkout={setViewWorkout}
            onViewRun={setViewRun}
            onSaveTemplate={async (template) => {
              data.setUserTemplates((prev) => [template, ...prev]);
              try { await db.insertUserTemplate(auth.userId, template); } catch (e) {
                console.error("Failed to save template:", e);
              }
            }}
            onDeleteTemplate={async (id) => {
              data.setUserTemplates((prev) => prev.filter((t) => t.id !== id));
              try { await db.deleteUserTemplate(id); } catch (e) {
                console.error("Failed to delete template:", e);
              }
            }}
            runPlan={data.runPlan}
            runPlanLoading={data.runPlanLoading}
            onGenerateRunPlan={runActions.handleGenerateRunPlan}
            onDeleteRunPlan={runActions.handleDeleteRunPlan}
            initialSubTab={trainSubTab}
            onSubTabConsumed={() => setTrainSubTab(null)}
          />
        )}

        {tab === "run" && (
          <RunTab
            runs={data.runs}
            runPlan={data.runPlan}
            runPlanLoading={data.runPlanLoading}
            onLogRun={(scheduled) => { setScheduledRunForLog(scheduled || null); setModal("logRun"); }}
            onViewRun={setViewRun}
            onGenerateRunPlan={runActions.handleGenerateRunPlan}
            onDeleteRunPlan={runActions.handleDeleteRunPlan}
          />
        )}

        {tab === "nutrition" && (
          <MealsTab
            allMeals={allMeals}
            customMeals={data.customMeals}
            todayMeals={todayMeals}
            todayTotals={todayTotals}
            mealLog={data.mealLog}
            macroGoals={todayGoals}
            isTrainingDay={todayHasWorkout}
            userProfile={data.userProfile}
            targetWeight={data.targetWeight}
            weightLog={data.weightLog}
            onLogMeal={mealActions.logMeal}
            onRemove={mealActions.removeMealEntry}
            onUpdateServings={mealActions.updateMealServings}
            onQuickAdd={mealActions.quickAddMeal}
            onSaveAndLogApiFood={mealActions.saveAndLogApiFood}
            onAddCustomMeal={() => setModal("customMeal")}
            onEditMacroGoals={() => setModal("macroGoals")}
            onDeleteCustomMeal={async (id) => {
              data.setCustomMeals((prev) => prev.filter((m) => m.id !== id));
              data.setMealLog((prev) => {
                const updated = {};
                for (const [date, entries] of Object.entries(prev)) {
                  const filtered = entries.filter((e) => e.mealId !== id);
                  if (filtered.length > 0) updated[date] = filtered;
                }
                return updated;
              });
              try { await db.deleteCustomMeal(id); } catch (e) {
                console.error("Failed to delete custom meal:", e);
              }
            }}
          />
        )}

        {tab === "profile" && (
          <ProfileTab
            userId={auth.userId}
            workouts={data.workouts}
            exercises={data.exercises}
            mealLog={data.mealLog}
            personalRecords={data.personalRecords}
            runs={data.runs}
            weightLog={data.weightLog}
            targetWeight={data.targetWeight}
            onLogWeight={() => setModal("logWeight")}
            onDeleteWeightEntry={async (id) => {
              data.setWeightLog((prev) => prev.filter((e) => e.id !== id));
              try { await db.deleteWeightEntry(id); } catch (e) {
                console.error("Failed to delete weight entry:", e);
              }
            }}
            onUpdateTargetWeight={async (tw) => {
              data.setTargetWeight(tw);
              try {
                await db.updateWeightSettings(auth.userId, { targetWeight: tw });
              } catch (e) { console.error("Failed to save target weight:", e); }
            }}
            userLevel={gamification.userLevel}
            levelProgress={gamification.levelProgress}
            achievements={gamification.achievements}
            userProfile={data.userProfile}
            onUpdateProfile={async (formData) => {
              await db.saveOnboarding(auth.userId, formData);
              data.setUserProfile(formData);
              const effectiveTarget = formData.targetWeight
                ? parseFloat(formData.targetWeight) || data.targetWeight
                : data.targetWeight;
              if (formData.targetWeight && parseFloat(formData.targetWeight) > 0 &&
                  parseFloat(formData.targetWeight) !== data.targetWeight) {
                data.setTargetWeight(parseFloat(formData.targetWeight));
                db.updateWeightSettings(auth.userId, { targetWeight: parseFloat(formData.targetWeight) }).catch((e) => console.error("Failed to save target weight:", e));
              }
              if (formData.weight && formData.age && formData.sex && formData.heightFeet) {
                const newMacros = calculateMacros({
                  weight: formData.weight,
                  heightFeet: formData.heightFeet,
                  heightInches: formData.heightInches,
                  age: formData.age,
                  sex: formData.sex,
                  trainingDaysPerWeek: formData.trainingDaysPerWeek,
                  goal: formData.primaryGoal,
                  targetWeight: effectiveTarget,
                });
                const goals = newMacros.daily;
                data.setMacroGoals(goals);
                try { await db.updateMacroGoals(auth.userId, goals); } catch (e) {
                  console.error("Failed to update macro goals:", e);
                }
              }
            }}
          />
        )}
        </Suspense>

        {/* Evening note — deliberately last on the page. Nothing precedes how
            the person is doing today, least of all a count. */}
        {tab === "home" && gamification.currentStreak > 0 && (
          <EveningNote
            currentStreak={gamification.currentStreak}
            hasActivityToday={hasActivityToday}
            onQuickAction={(action) => {
              if (action === "workout") setModal("newWorkout");
              else if (action === "meal") setTab("nutrition");
            }}
          />
        )}

        <BottomNav tab={tab} setTab={setTab} />

        {/* Coach entry point. It is a door, not a nag: no pulse, no running
            macro total, and no floating label — a permanent text pill sat on
            top of a line of the page at every scroll offset and only repeated
            the button's own accessible name. The prompt still shapes what the
            coach is asked, it just isn't printed over the content. */}
        {!workoutActions.activeWorkout && !showChat && !workoutActions.postWorkoutData && (() => {
          const trainedToday = data.workouts.some((w) => dateKey(w.startTime) === todayKey());

          let fabPrompt;
          if (tab === "nutrition") {
            fabPrompt = "What should I eat?";
          } else if (tab === "train" && !trainedToday) {
            fabPrompt = "Plan something gentle";
          } else if (tab === "train") {
            fabPrompt = "How was that?";
          } else if (tab === "run") {
            fabPrompt = "Help with running";
          } else if (tab === "home" && !trainedToday) {
            fabPrompt = "What should I do today?";
          } else {
            fabPrompt = "Ask your coach";
          }

          return (
            <div className="shell-fab-wrap">
              <button
                type="button"
                onClick={() => setShowChat(true)}
                className="shell-fab"
                aria-label={`Ask your coach — ${fabPrompt}`}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              </button>
            </div>
          );
        })()}
      </div>

      <Suspense fallback={null}>
      {showChat && (
        <CoachChat
          userId={auth.userId}
          onClose={() => setShowChat(false)}
          onSendMessage={(messages, context) => aiLib.sendChatMessage(auth.userId, messages, context)}
          onSendMessageStream={(messages, context, onDelta) => aiLib.sendChatMessageStream(auth.userId, messages, context, onDelta)}
          exercises={data.exercises}
          allMeals={allMeals}
          workouts={data.workouts}
          runs={data.runs}
          mealLog={data.mealLog}
          personalRecords={data.personalRecords}
          moodCheckins={data.moodCheckins}
          weightLog={data.weightLog}
          todayTotals={todayTotals}
          todayBurn={todayBurn}
          macroGoals={todayGoals}
          currentStreak={gamification.currentStreak}
          targetWeight={data.targetWeight}
          userProfile={data.userProfile}
          runPlan={data.runPlan}
          onBeginWorkout={(aiWorkout) => {
            workoutActions.handleBeginChatWorkout(aiWorkout);
            setShowChat(false);
          }}
          onLogMeal={mealActions.logMeal}
          onQuickAdd={mealActions.quickAddMeal}
        />
      )}

      {workoutActions.postWorkoutData && (
        <PostWorkoutSummary
          workout={workoutActions.postWorkoutData.workout}
          exercises={data.exercises}
          sessionPrs={workoutActions.postWorkoutData.prs}
          insight={workoutActions.workoutInsight}
          insightLoading={workoutActions.insightLoading}
          userWeight={data.userProfile?.weight || (data.weightLog || [])[0]?.weight}
          workouts={data.workouts}
          personalRecords={data.personalRecords}
          onDone={() => {
            workoutActions.dismissPostWorkout();
            aiLib.getProactiveInsights(auth.userId)
              .then((result) => { if (result?.insights?.length) data.setAiInsights?.(result.insights); })
              .catch(() => {});
          }}
          onLogNutrition={() => {
            workoutActions.dismissPostWorkout();
            setTab("nutrition");
          }}
          onShare={(shareData) => {
            workoutActions.dismissPostWorkout();
            setShareCard(shareData);
          }}
        />
      )}

      {streakMilestone && (
        <StreakMilestone
          streak={streakMilestone}
          onDismiss={() => {
            markStreakMilestoneShown(streakMilestone);
            setStreakMilestone(null);
          }}
        />
      )}

      {prCelebration && (
        <PRCelebration
          pr={prCelebration}
          nextMilestone={
            prCelebration.exerciseId
              ? getNextMilestone(prCelebration.exerciseId, prCelebration.weight, data.workouts || [])
              : null
          }
          onDone={() => setPrCelebration(null)}
          onShare={() => {
            setShareCard({
              type: "pr",
              data: {
                exerciseName: prCelebration.exerciseName,
                weight: prCelebration.weight,
                reps: prCelebration.reps,
                previousWeight: prCelebration.previousWeight,
              },
            });
            setPrCelebration(null);
          }}
        />
      )}

      {workoutActions.activeWorkout && (
        <ActiveWorkout
          workout={workoutActions.activeWorkout}
          exercises={data.exercises}
          onChange={workoutActions.setActiveWorkout}
          onFinish={workoutActions.finishWorkout}
          /* The way out of a session that isn't happening today. Parks it —
             never onFinish, which would record a workout that didn't. */
          onLeave={() => workoutActions.setActiveWorkout(null)}
          onAddExercise={() => setModal("addExercise")}
          onCheckPR={handleCheckPR}
          personalRecords={data.personalRecords}
          pastWorkouts={data.workouts}
        />
      )}

      {viewRun && (
        <RunDetail
          run={viewRun}
          onClose={() => setViewRun(null)}
          onDelete={(id) => { runActions.handleDeleteRun(id); setViewRun(null); }}
        />
      )}

      {viewWorkout && (
        <WorkoutDetail
          workout={viewWorkout}
          exercises={data.exercises}
          onClose={() => setViewWorkout(null)}
          onDelete={(id) => { workoutActions.handleDeleteWorkout(id); setViewWorkout(null); }}
          onSaveTemplate={async (w) => {
            const template = {
              id: genId(),
              name: w.name,
              exerciseIds: (w.exerciseBlocks || []).map((b) => b.exerciseId),
            };
            data.setUserTemplates((prev) => [template, ...prev]);
            setViewWorkout(null);
            try { await db.insertUserTemplate(auth.userId, template); } catch (e) {
              console.error("Failed to save template:", e);
            }
          }}
        />
      )}

      {modal === "newWorkout" && (
        <NewWorkoutModal
          templates={DEFAULT_TEMPLATES}
          userTemplates={data.userTemplates}
          exercises={data.exercises}
          workouts={data.workouts}
          onStart={(w) => { workoutActions.setActiveWorkout(w); setModal(null); }}
          onClose={() => setModal(null)}
          onDeleteTemplate={async (id) => {
            data.setUserTemplates((prev) => prev.filter((t) => t.id !== id));
            try { await db.deleteUserTemplate(id); } catch (e) {
              console.error("Failed to delete template:", e);
            }
          }}
        />
      )}

      {modal === "addExercise" && (
        <AddExerciseModal
          exercises={data.exercises}
          onAdd={(ids) => {
            workoutActions.setActiveWorkout((aw) => ({
              ...aw,
              exerciseBlocks: [
                ...aw.exerciseBlocks,
                ...ids.map((id) => ({ exerciseId: id, sets: [{ reps: "", weight: "", done: false }] })),
              ],
            }));
            setModal(null);
          }}
          onClose={() => setModal(null)}
        />
      )}

      {modal === "newExercise" && (
        <NewExerciseModal
          onSave={async (ex) => {
            data.setExercises((prev) => [...prev, ex]);
            setModal(null);
            try { await db.insertUserExercise(auth.userId, ex); } catch (e) {
              console.error("Failed to save exercise:", e);
            }
          }}
          onClose={() => setModal(null)}
        />
      )}

      {modal === "customMeal" && (
        <CustomMealModal
          onSave={async (meal) => {
            data.setCustomMeals((prev) => [...prev, meal]);
            setModal(null);
            try { await db.insertCustomMeal(auth.userId, meal); } catch (e) {
              console.error("Failed to save custom meal:", e);
            }
          }}
          onClose={() => setModal(null)}
        />
      )}

      {modal === "logRun" && (
        <LogRunModal
          scheduledRun={scheduledRunForLog}
          userWeight={data.userProfile?.weight || (data.weightLog || [])[0]?.weight}
          onSave={async (run) => {
            data.setRuns((prev) => [run, ...prev]);
            setModal(null);
            setScheduledRunForLog(null);
            hapticMedium();
            toast.success("Run logged");
            track("run_logged", { distance: run.distance, unit: run.unit });
            syncRunToHealth(run).catch((e) => console.error("HealthKit run sync failed:", e));
            try {
              await db.insertRun(auth.userId, run);
            } catch (e) {
              console.error("Failed to save run:", e);
              if (!isOnline) {
                enqueue({ type: "run", payload: { userId: auth.userId, run } });
                setPendingSyncs(getPendingCount());
              }
            }
          }}
          onClose={() => { setModal(null); setScheduledRunForLog(null); }}
        />
      )}

      {modal === "logWeight" && (
        <LogWeightModal
          alreadyLoggedToday={data.weightLog.some((e) => e.date === todayKey())}
          targetWeight={data.targetWeight}
          reminderTime={data.weightReminderTime}
          onSave={async (entry) => {
            data.setWeightLog((prev) => [entry, ...prev]);
            setModal(null);
            hapticMedium();
            toast.success("Weight logged");
            syncWeightToHealth(entry.weight).catch((e) => console.error("HealthKit weight sync failed:", e));
            try {
              await db.insertWeightEntry(auth.userId, entry);
            } catch (e) {
              console.error("Failed to save weight entry:", e);
              if (!isOnline) {
                enqueue({ type: "weight", payload: { userId: auth.userId, entry } });
                setPendingSyncs(getPendingCount());
              }
            }
          }}
          onSaveSettings={async (settings) => {
            if (settings.targetWeight !== undefined) data.setTargetWeight(settings.targetWeight);
            if (settings.reminderTime !== undefined) data.setWeightReminderTime(settings.reminderTime);
            try { await db.updateWeightSettings(auth.userId, settings); } catch (e) {
              console.error("Failed to save weight settings:", e);
            }
          }}
          onClose={() => setModal(null)}
        />
      )}

      {modal === "macroGoals" && (
        <MacroGoalsModal
          currentGoals={data.macroGoals}
          userProfile={data.userProfile}
          currentWeight={data.weightLog[0]?.weight}
          targetWeight={data.targetWeight}
          onSave={async (goals) => {
            data.setMacroGoals(goals);
            setModal(null);
            try { await db.updateMacroGoals(auth.userId, goals); } catch (e) {
              console.error("Failed to save macro goals:", e);
            }
          }}
          onClose={() => setModal(null)}
        />
      )}

      {shareCard && (
        <ShareCard
          type={shareCard.type}
          data={shareCard.data}
          onClose={() => setShareCard(null)}
        />
      )}
      </Suspense>
    </>
  );
}
